import posthog from "posthog-js"

// Cookieless PostHog analytics.
//
// The whole point of this setup is to collect anonymous usage stats WITHOUT
// storing anything on the visitor's device, so the site needs no cookie/consent
// banner. The two init options that make that true:
//
//   persistence: "memory"      -> the distinct-id lives in memory only, so
//                                 PostHog never writes ph_* cookies or
//                                 localStorage entries.
//   disable_persistence: true  -> belt-and-suspenders: nothing is persisted
//                                 client-side across page loads.
//
// Trade-off: with memory-only persistence every page load gets a fresh
// anonymous distinct-id, so PostHog cannot stitch a visitor across a session or
// across visits. You get anonymous pageview/event counts, not unique-user or
// funnel continuity. That is the intended, privacy-preserving behaviour.

const posthogApiKey = process.env.GATSBY_POSTHOG_PROJECT_TOKEN
const posthogHost = process.env.GATSBY_POSTHOG_HOST

const isDev = process.env.NODE_ENV === "development"

class PostHogAnalytics {
  constructor() {
    this.enabled = false

    // Gatsby renders this module during SSR too — bail out when there is no
    // browser.
    if (typeof window === "undefined") return

    // In development we deliberately do NOT initialise PostHog. Calling
    // posthog.init() makes network requests to PostHog on its own — it loads
    // remote config (GET /array/<key>/config.js) and feature flags
    // (POST /flags/) — and those fire regardless of how events are handled.
    // With a placeholder dev key they 404/401 and spam the console. Instead we
    // just log events locally (see the methods below) so you can see them fire
    // without ever contacting PostHog in dev.
    if (isDev) return

    // Stay a harmless no-op if no key is configured.
    if (!posthogApiKey || !posthogHost) return

    posthog.init(posthogApiKey, {
      api_host: posthogHost,
      persistence: "memory",
      disable_persistence: true,
      capture_pageview: false, // pageviews are fired manually on route change
      advanced_disable_flags: true, // pageview-only analytics: skip the feature-flag/decide request
    })

    this.enabled = true
  }

  pageView(pathname) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log("[posthog:dev] $pageview", pathname)
      return
    }
    if (!this.enabled) return
    const properties = pathname
      ? { $current_url: `${window.location.origin}${pathname}` }
      : {}
    posthog.capture("$pageview", properties)
  }

  track(event, properties) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log("[posthog:dev]", event, properties)
      return
    }
    if (!this.enabled) return
    posthog.capture(event, properties)
  }

  identify(id, properties) {
    if (isDev || !this.enabled) return
    posthog.identify(id, properties)
  }

  reset() {
    if (isDev || !this.enabled) return
    posthog.reset()
  }
}

export const posthogAnalytics = new PostHogAnalytics()
